import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SEO from "@/components/SEO";
import AdminHeader from "@/components/admin/AdminHeader";
import AdminTabs from "@/components/admin/AdminTabs";
import AdminRecordsList from "@/components/admin/AdminRecordsList";
import AdminRecordDetail from "@/components/admin/AdminRecordDetail";
import AdminAnalytics from "@/components/admin/AdminAnalytics";
import AdminSeoAudit from "@/components/admin/AdminSeoAudit";
import AdminPortfolio from "@/components/admin/AdminPortfolio";
import AdminCareerListings from "@/components/admin/AdminCareerListings";
import AdminCareerApplications from "@/components/admin/AdminCareerApplications";
import AdminCommercialInquiries from "@/components/admin/AdminCommercialInquiries";
import AdminProjectListings from "@/components/admin/AdminProjectListings";
import AdminPartnerApplications from "@/components/admin/AdminPartnerApplications";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useAdminData } from "@/hooks/useAdminData";
import { adminClient } from "@/lib/apiClient";

const RECORD_TABS = [
  { key: "leads", label: "Leads", endpoint: "/admin/leads", deleteEndpoint: "/admin/leads" },
  { key: "maintenance", label: "Maintenance Signups", endpoint: "/admin/maintenance-signups", deleteEndpoint: "/admin/maintenance-signups" },
  { key: "subcontractors", label: "Subcontractor Applications", endpoint: "/admin/subcontractors", deleteEndpoint: "/admin/subcontractors" },
];

const ANALYTICS_TAB = { key: "analytics", label: "Analytics" };
const SEO_TAB = { key: "seo", label: "SEO Health" };
const PORTFOLIO_TAB = { key: "portfolio", label: "Portfolio" };
const CAREER_LISTINGS_TAB = { key: "career-listings", label: "Career Listings" };
const CAREER_APPS_TAB = { key: "career-apps", label: "Career Applications" };
const COMMERCIAL_TAB = { key: "commercial-inquiries", label: "Commercial Inquiries" };
const PROJECT_LISTINGS_TAB = { key: "project-listings", label: "Project Listings" };
const PARTNER_APPS_TAB = { key: "partner-apps", label: "Partner Applications" };

const ALL_TABS = [...RECORD_TABS, PORTFOLIO_TAB, CAREER_LISTINGS_TAB, CAREER_APPS_TAB, COMMERCIAL_TAB, PROJECT_LISTINGS_TAB, PARTNER_APPS_TAB, ANALYTICS_TAB, SEO_TAB];

function RecordsView({ tab, tabConfig, onLogout, navigate, status }) {
  const [active, setActive] = useState(null);
  const { items, loading, error, reload } = useAdminData(tabConfig.endpoint, [tab]);

  // Reset active when switching record tabs
  useEffect(() => { setActive(null); }, [tab]);

  // Auth redirect
  useEffect(() => {
    if (status === "guest" || error === "unauthorized") navigate("/admin/login");
  }, [status, error, navigate]);

  // For subcontractors the list endpoint omits the large file payloads; fetch
  // the full record (including files) when a row is selected.
  const handleSelect = useCallback(async (record) => {
    if (!record) { setActive(null); return; }
    if (tab !== "subcontractors") { setActive(record); return; }
    // Set a quick preview, then upgrade with full data
    setActive(record);
    try {
      const res = await adminClient.get(`/admin/subcontractors/${record.id}`);
      setActive(res.data);
    } catch (_) { /* keep preview state */ }
  }, [tab]);

  const handleUpdateStatus = useCallback(async (id, newStatus) => {
    try {
      await adminClient.patch(`/admin/leads/${id}`, { status: newStatus });
      await reload();
      setActive((prev) => (prev && prev.id === id ? { ...prev, status: newStatus } : prev));
    } catch (_) { /* surfaced via error state on next reload */ }
  }, [reload]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await adminClient.delete(`${tabConfig.deleteEndpoint}/${id}`);
      await reload();
      setActive((prev) => (prev && prev.id === id ? null : prev));
    } catch (_) { /* same */ }
  }, [tabConfig.deleteEndpoint, reload]);

  return (
    <>
      {error && error !== "unauthorized" && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded mb-4">{error}</div>
      )}
      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7 bg-white rounded border border-gray-200 overflow-hidden">
          <AdminRecordsList items={items} loading={loading} active={active} onSelect={handleSelect} />
        </div>
        <div className="lg:col-span-5">
          <AdminRecordDetail
            active={active}
            tab={tab}
            onDelete={handleDelete}
            onUpdateStatus={handleUpdateStatus}
          />
        </div>
      </div>
    </>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { status, logout } = useAdminAuth();
  const [tab, setTab] = useState("leads");

  // Redirect to login when not authed
  useEffect(() => {
    if (status === "guest") navigate("/admin/login");
  }, [status, navigate]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate("/admin/login");
  }, [logout, navigate]);

  // Refresh action — only meaningful for record tabs.
  // Records component owns its own reload via the hook; we no-op here.
  const handleRefresh = useCallback(() => {
    // Trigger a remount of the records view by toggling tab via key change
    // is handled inside AdminAnalytics for analytics. For records, the data
    // is fetched on tab change. So as a low-cost refresh, force a state cycle:
    setTab((t) => t);
  }, []);

  const recordTab = RECORD_TABS.find((t) => t.key === tab);

  return (
    <>
      <SEO title="Admin Dashboard" noIndex />
      <div className="min-h-screen bg-br-paper">
        <AdminHeader onRefresh={handleRefresh} onLogout={handleLogout} />
        <div className="container-br py-6">
          <AdminTabs tabs={ALL_TABS} active={tab} onChange={setTab} />
          {recordTab && (
            <RecordsView
              key={tab}
              tab={tab}
              tabConfig={recordTab}
              onLogout={handleLogout}
              navigate={navigate}
              status={status}
            />
          )}
          {tab === "analytics" && <AdminAnalytics />}
          {tab === "seo" && <AdminSeoAudit />}
          {tab === "portfolio" && <AdminPortfolio />}
          {tab === "career-listings" && <AdminCareerListings />}
          {tab === "career-apps" && <AdminCareerApplications />}
          {tab === "commercial-inquiries" && <AdminCommercialInquiries />}
          {tab === "project-listings" && <AdminProjectListings />}
          {tab === "partner-apps" && <AdminPartnerApplications />}
        </div>
      </div>
    </>
  );
}
